<template>
  <div>
    <div class="mb-4">
      <div class="mb-3">
        <SearchBar
          ref="component__search-bar"
          v-model="searchKeyWords"
          placeholder="Search micro-credentials here..."
          @reset="onSearchReset"
        />
      </div>
      <Transition name="slide-fade--up" appear mode="out-in">
        <div key="displaying">
          <div v-if="isStateChanging" key="loading" class="d-flex justify-content-center p-4 m-3">
            <b-spinner label="Loading..." variant="dark" />
          </div>

          <span v-else>
            <BaseLists
              ref="component__base-lists"
              :items="learningUnits"
              :count-lazy-loading="countLazyLoading"
              :is-loading="isLoading"
              :no-more-item="noMoreItem"
              :toggle-fetching="toggleFetching"
              @clickItem="goDesignAndPlanningInfoPage($event)"
              @fetchNewItems="fetchMorelearningUnits"
            >
              <template v-slot:thumbnail="learningUnit">
                <mc-explore-thumbnail
                  ref="component__mc-explore-thumbnail"
                  :key="`thumbnail--${learningUnit.item.id}`"
                  :learning-unit="learningUnit.item"
                />
              </template>

              <template v-slot:list-detail="learningUnit">
                <div ref="text__learning-unit-name" class="font-size-lg">
                  {{ learningUnit.item.name }}
                </div>
                <div v-if="learningUnit.item.level_type" class="text-primary font-weight-bold mt-2">
                  <span class="mr-1"
                    ><img
                      ref="icon__learning-unit-type"
                      :src="
                        learningUnit.item.level_type === 'CAREER_SPECIFIC'
                          ? require('@/assets/learning-unit-types/specific.svg')
                          : require('@/assets/learning-unit-types/academic.svg')
                      "
                      style="height: 16px"
                    />
                  </span>
                  <span ref="text__learning-unit-type" class="text__mc-type">{{
                    learningUnit.item.level_type === 'CAREER_SPECIFIC'
                      ? 'Career Specific'
                      : 'Academic Focused'
                  }}</span>
                </div>

                <div class="d-flex mt-u4">
                  <learning-unit-status-badge
                    ref="badge__learning-unit-status"
                    :learning-unit="learningUnit.item"
                  />
                </div>
              </template>
              <template v-slot:list-options="learningUnit">
                <BaseEllipsis
                  v-if="!(learningUnit.item.is_published || learningUnit.item.required_review)"
                >
                  <b-dropdown-item
                    v-if="learningUnit.item.canEdit(userId)"
                    @click.prevent.stop="goEditDesignAndPlanningPage(learningUnit.item.id)"
                  >
                    Edit
                  </b-dropdown-item>
                  <!-- <b-dropdown-item
                    v-if="learningUnit.item.canDelete(userId)"
                    @click.prevent.stop="showModal(`modalDelete__${learningUnit.item.id}`)"
                  >
                    Delete
                  </b-dropdown-item> -->
                </BaseEllipsis>

                <ModalDelete
                  v-if="learningUnit.item.canDelete(userId)"
                  :ref="`modalDelete__${learningUnit.item.id}`"
                  :learning-unit="learningUnit.item"
                  @isDeleted="isDeleted(learningUnit.index)"
                />
              </template>
            </BaseLists>

            <div
              v-if="!hasData"
              ref="container__empty-search-result"
              class="text-dark mr-auto px-2"
            >
              <div class="my-4">
                Sorry, we couldn't find any results
                <span class="d-block">Try adjusting your search, Here are some ideas:</span>
              </div>
              <ul>
                <li>Make sure all words are spelled correctly</li>
                <li>Try different search terms</li>
                <li>Try more general search terms</li>
              </ul>
            </div>
          </span>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { map } from 'lodash';

/* Base Components */
import { BaseEllipsis, BaseLists } from '@/components/base-components';

/* Customized Components */
import { ModalDelete } from '../../LearningUnit/components';
import McExploreThumbnail from '../../LearningUnit/components/McExploreThumbnail.vue';
import LearningUnitStatusBadge from '@/components/LearningUnit/LearningUnitStatusBadge.vue';
import SearchBar from '../../LearningUnit/components/SearchBar.vue';

/* Mixins */
import { userInfoMixin } from '@/mixins';

/* Models */
import LearningUnit from '@/models/LearningUnit';
import User from '@/models/User';

export default {
  components: {
    /* BootstrapVue Components */
    // BDropdownItem,
    /* Base Components */
    BaseEllipsis,
    BaseLists,
    /* Customized Components */
    ModalDelete,
    McExploreThumbnail,
    LearningUnitStatusBadge,
    SearchBar
  },

  mixins: [userInfoMixin],

  data() {
    return {
      /**
       * Owning learning units data that recieved from fetch API
       *
       * @type {Object[]}
       */
      learningUnitIds: [],

      /**
       * Show/Hide spinner when fetching data
       *
       * @type {Boolean}
       */
      isLoading: false,

      /**
       * Show/Hide spinner when state change
       *
       * @type {Boolean}
       */
      isStateChanging: false,

      /**
       * Count amount of time lazy loading has been trigger
       *
       * @type {Number}
       */
      countLazyLoading: 0,

      /**
       * Become true when no more learning unit to fetch,
       * Use to prevent fetching when no learning unit to fetch
       *
       * @type {Boolean}
       */
      noMoreItem: false,

      /**
       * state for check fetching more than 15 items
       *
       * @type {Boolean}
       */
      toggleFetching: true,
      searchKeyWords: [],
      hasData: false
    };
  },

  computed: {
    learningUnits() {
      return this.learningUnitIds
        .map((learningUnitId) =>
          LearningUnit.query().with('media_detail_vimeo').find(learningUnitId)
        )
        .filter(Boolean);
    },
    fetchParams() {
      const filter = {};

      if (this.searchKeyWords.length) this.$set(filter, 'name', this.searchKeyWords.join(' '));

      return filter;
    }
  },

  watch: {
    userId: {
      immediate: true,
      async handler(userId) {
        await User.api().updateAuthLearningUnitMemberState();
        await this.fetchUserlearningUnits(userId);
      }
    },
    searchKeyWords: {
      async handler() {
        this.isStateChanging = true;

        await this.fetchOnStateChanged();
      },
      deep: true
    }
  },

  async mounted() {
    this.countLazyLoading = 0;
    this.noMoreItem = false;
    this.isStateChanging = true;
  },

  methods: {
    /**
     * Fetching less than or equal to 15 learning units with user uid
     */
    async fetchUserlearningUnits(userId) {
      if (!userId) return;

      try {
        this.isLoading = true;

        let start = 0;
        let limit = 15;

        if (this.countLazyLoading > 0) {
          start = 15 * this.countLazyLoading;
        }

        const { response } = await LearningUnit.api().findByDesigner(
          this.fetchParams,
          start,
          limit
        );

        if (response.data && response.data.length) {
          this.learningUnitIds = [
            ...new Set([...this.learningUnitIds, ...map(response.data, 'id')])
          ];

          this.countLazyLoading++;
        } else {
          this.noMoreItem = true;
        }
      } catch (error) {
        console.log(error);

        this.$toast.error('Cannot load your learning units, please try again');
      } finally {
        this.hasData = this.learningUnitIds.length > 0;
        this.isLoading = false;
        this.isStateChanging = false;
      }
    },

    /**
     * Fetching more learning units with or without filter
     *
     * @param {start} Number data index for start fetching
     * @param {limit} Number limit amount of datas for fetching
     */
    async fetchMorelearningUnits() {
      this.toggleFetching = true;

      await this.fetchUserlearningUnits(this.userId);

      setTimeout(() => {
        this.toggleFetching = false;
      }, 500);
    },

    /**
     * filter out LU that was deleted.
     *
     * @param {Object} learningUnit Recieve LU from BaseLists
     */
    isDeleted(index) {
      this.learningUnitIds.splice(index, 1);
    },

    /**
     * Redirect to LU's detail page
     *
     * @param {Object} LearningUnit Recieve LU from BaseLists
     */
    goDesignAndPlanningInfoPage(learningUnit) {
      this.$router.push({
        name: 'design-planning/info',
        params: {
          learningUnitId: learningUnit.id
        },
        query: {
          tab: 'basic-information'
        }
      });
    },

    /**
     * Redirect to edit LU's page
     *
     * @param {Number} learningUnitId Recieve learningUnitId from BaseLists
     */
    goEditDesignAndPlanningPage(learningUnitId) {
      const baseUrl = process.env.VUE_APP_CATALOG_APP_URL;

      if (!baseUrl || !learningUnitId) {
        console.error('Missing base URL or learning unit ID');
        return;
      }

      const url = new URL(`/ets-4life/learning-units/${learningUnitId}/edit`, baseUrl);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    },

    /**
     * Trigger open modal component
     *
     * @param {String} modalType type Read,Update,Delete that concat with learningUnitId
     */
    showModal(modalType) {
      this.$refs[modalType].show(modalType);
    },

    getLearningUnitStatus(learningUnit) {
      let status = 'Draft';

      if (learningUnit.required_review) {
        status = 'Submitted for Publication';
      }

      if (learningUnit.is_published) {
        status = 'Published';
      }

      return status;
    },

    statusVariant(learningUnit) {
      let variant = 'danger-dark';

      if (learningUnit.required_review) {
        variant = 'neutral-warning';
      }

      if (learningUnit.is_published) {
        variant = 'neutral-success';
      }

      return variant;
    },

    statusClass(learningUnit) {
      return {
        'badge__lu-draft-status text-dark':
          !learningUnit.required_review && !learningUnit.is_published,
        'badge__lu-submitted-for-publication-status text-warning': learningUnit.required_review,
        'badge__lu-published-status text-success': learningUnit.is_published
      };
    },
    async fetchOnStateChanged() {
      try {
        this.toggleFetching = true;
        this.countLazyLoading = 0;
        this.noMoreItem = false;
        this.learningUnitIds = [];
        await this.fetchUserlearningUnits(this.userId);
      } catch (error) {
        this.$toast('Cannot load data, please try again');
      }
    },
    onSearchReset() {
      this.searchKeyWords = [];
    }
  }
};
</script>

<style lang="scss">
.custom-height {
  height: 100px !important;
}

.img-size {
  max-width: 160px;
  height: 100px;
  max-height: max-content;
}

// .img-size {
//   max-width: 160px;
//   max-height: 100px;
//   width: 100%;
// }

// @media only screen and (max-width: 576px) {
//   .img-size {
//     width: 320px;
//     height: 200px;
//     max-height: max-content;
//   }
//   .custom-height {
//     height: 120px !important;
//   }
// }
</style>
